import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Ceiling for Cost / Retail. Must match ProductController::PRICE_MAX in the
 * backend: it catches typos and stays far below the decimal(10,2) column limit.
 */
export const PRICE_MAX = 9999999.99;

/** PRICE_MAX as the user sees it, for the ceiling message. */
export const PRICE_MAX_LABEL = PRICE_MAX.toLocaleString('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Strips the formatting the edit dialog loads values with ("$1,234.56"). */
function cleanPrice(raw: unknown): string {
  return String(raw ?? '').replace(/[$,\s]/g, '');
}

/**
 * Cost / Retail input rule: optional, a plain non-negative number not above
 * PRICE_MAX. More than 2 decimals is NOT an error: the field rounds itself to
 * 2 places on blur (roundPriceControl) so the user sees what will be saved.
 */
export function priceValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const text = cleanPrice(control.value);
    if (text === '') {
      return null;
    }
    if (!/^(\d+(\.\d*)?|\.\d+)$/.test(text)) {
      return { priceInvalid: true };
    }
    if (parseFloat(text) > PRICE_MAX) {
      return { priceMax: true };
    }
    return null;
  };
}

/**
 * On blur: shows the value rounded to 2 decimals in the box, e.g. 12.345 -> 12.35.
 * Decimal-string rounding (exponent shift) avoids binary float artefacts such as
 * 9.005 -> 9.00. Invalid or empty input is left alone for the validator to report.
 */
export function roundPriceControl(control: AbstractControl | null): void {
  if (!control) {
    return;
  }
  const text = cleanPrice(control.value);
  if (text === '' || !/^(\d+(\.\d*)?|\.\d+)$/.test(text)) {
    return;
  }
  const rounded = (Math.round(Number(text + 'e2')) / 100).toFixed(2);
  if (rounded !== text) {
    control.setValue(rounded);
  }
}
